import React, { useEffect, useRef } from 'react';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { caseStudies } from '../mock';
import { ExternalLink, TrendingUp, FileText } from 'lucide-react';

const CaseStudies = () => {
  const studiesRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = studiesRef.current?.querySelectorAll('.fade-in');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const extractTweetId = (url) => {
    const match = url?.match(/status\/(\d+)/);
    return match ? match[1] : null;
  };

  return (
    <section className="case-studies-section" ref={studiesRef}>
      <div className="case-studies-container">
        <h2 className="section-title-clean fade-in">Select Work</h2>
        <p className="section-subtitle-clean fade-in">High-impact Transformative Work</p>

        {caseStudies.map((study, index) => (
          <div
            key={study.id}
            className={`case-study fade-in ${study.alignment === 'right' ? 'case-study-reverse' : ''}`}
          >
            {/* Image/Media Side */}
            <div className="case-study-media">
              {study.citiReport && (
                <div className="avail-nexus-frame">
                  <div className="nexus-header">
                    <span>Avail Nexus Mainnet</span>
                  </div>
                  <div className="nexus-tweet">
                    <TwitterTweetEmbed
                      tweetId={extractTweetId(study.twitterVideoUrl)}
                      options={{ theme: 'light', width: '100%' }}
                    />
                  </div>
                </div>
              )}
              
              {study.dashboardMockup && (
                <div className="featured-frame">
                  <div className="featured-header">
                    <span>Featured</span>
                  </div>
                  <div className="featured-image">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_maximalist-marketing/artifacts/u98btzwa_Coindesk.png"
                      alt="Neon EVM Partners with deBridge Protocol - CoinDesk"
                      className="coindesk-image"
                    />
                  </div>
                </div>
              )}
              
              {study.twitterVideoUrl && !study.citiReport && !study.dashboardMockup && (
                <div className="gaming-tweet-frame">
                  <div className="gaming-header">
                    <span>{study.title}</span>
                  </div>
                  <div className="gaming-tweet">
                    <TwitterTweetEmbed
                      tweetId={extractTweetId(study.twitterVideoUrl)}
                      options={{ theme: 'light', width: '100%' }}
                    />
                  </div>
                </div>
              )}
              
              {study.videoUrl && (
                <div className="video-frame">
                  <div className="video-header">
                    <span>{study.title}</span>
                  </div>
                  <div className="video-content">
                    <video 
                      src={study.videoUrl} 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="case-study-video"
                    />
                  </div>
                </div>
              )}
              
              {study.placardImage && (
                <div className="image-frame">
                  <div className="image-header">
                    <span>{study.title}</span>
                  </div>
                  <div className="image-content">
                    <img 
                      src={study.placardImage} 
                      alt={study.title}
                      className="case-study-image"
                    />
                  </div>
                </div>
              )}
              
              {study.websiteEmbed && (
                <div className="website-embed-frame">
                  <div className="website-embed-header">
                    <span>{study.title}</span>
                  </div>
                  <div className="website-embed-content">
                    <iframe 
                      src={study.websiteEmbed}
                      title={study.title}
                      className="website-embed-iframe"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Content Side */}
            <div className="case-study-content">
              <span className="case-category">{study.category}</span>
              <h3 className="case-title">{study.title}</h3>
              <p className="case-subtitle">{study.subtitle}</p>
              <p className="case-description">{study.description}</p>
              
              <div className="case-metrics">
                {study.metrics.map((metric, idx) => (
                  <div key={idx} className="metric-item">
                    <span className="metric-label">{metric.label}</span>
                    <span className="metric-value">{metric.value}</span>
                  </div>
                ))}
              </div>
              
              {(study.websiteUrl || study.blogUrl) && (
                <div className="case-actions">
                  {study.websiteUrl && (
                    <a href={study.websiteUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
                      {study.title === 'Mudrex' ? 'View Work' :
                       study.title === 'Santage AI' ? 'Visit Santage' :
                       study.title === 'Neon EVM' ? 'Find More' : 
                       study.title === 'Fancy Studios' ? 'Learn More' : 
                       study.title === 'Emerging Tech' ? 'Get the Latest' :
                       study.title === 'Avail x Arcana' ? 'Find in News' : 'Visit Website'} <ExternalLink size={16} />
                    </a>
                  )}
                  {study.blogUrl && (
                    <a href={study.blogUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                      Read Articles <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CaseStudies;